import React from 'react';
import { EventSnapshot, Market, Outcome, TopEventItem } from '../../api/types';
import { SPORT_EMOJI } from './DesktopLeftSidebar';
import { getEventName } from '../../utils/eventHelpers';

export interface TopEventsCardProps {
  event: TopEventItem;
  onOddsClick?: (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => void;
  isSelected?: (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) => boolean;
  formatTime: (timeStr: string) => string;
}

export function formatOdds(val: string | number): string {
  if (val === undefined || val === null || val === '-') return '-';
  const num = typeof val === 'string' ? parseFloat(val.replace(',', '.')) : val;
  if (isNaN(num) || num < 1.01) return '-';
  return num.toFixed(2);
}

export const TopEventsCard: React.FC<TopEventsCardProps> = ({ event, onOddsClick, isSelected, formatTime }) => {
  const pm = event.primary_market;
  if (!pm) return null; // Safe fallback if API omits primary_market

  const mockMarket: Market = {
    market_id: pm.market_id,
    canonical_key: pm.canonical_key,
    name: pm.market_key,
    specifiers: pm.specifiers,
    outcomes: pm.outcomes,
    status: 1, // Default active status
    favourite: false
  };
  
  const home = pm.outcomes.find(o => o.outcome_id === '1' || o.name === '1' || o.name.toLowerCase() === 'home');
  const draw = pm.outcomes.find(o => o.outcome_id === '2' || o.name.toLowerCase() === 'draw' || o.name.toLowerCase() === 'x');
  const away = pm.outcomes.find(o => o.outcome_id === '3' || o.name === '2' || o.name.toLowerCase() === 'away');

  let dateDisplay = '';
  const scheduledStr = event.event.scheduled_at || event.event.start_time;
  if (scheduledStr) {
    const d = new Date(scheduledStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const timeStr = formatTime(scheduledStr);
    dateDisplay = `${day}/${month} ${timeStr}`;
  }
  
  const mockEventSnapshot: EventSnapshot = {
    event_id: event.event_id,
    event: event.event,
    markets: [mockMarket],
    scope: 'pre',
    version: 1,
    status: { home_score: 0, away_score: 0 }
  };

  const renderOddsButton = (label: string, outcome: Outcome | undefined, tipp: string) => {
    if (!outcome || !outcome.active || formatOdds(outcome.odds) === '-') {
      return null;
    }
    
    const sel = isSelected?.(event.event_id, pm.market_id, outcome.outcome_id, pm.specifiers) || false;
    const oddsVal = formatOdds(outcome.odds);
    
    return (
      <button 
        onClick={() => onOddsClick?.(mockEventSnapshot, mockMarket, outcome, tipp)}
        className={`flex-1 border rounded-[4px] h-[35px] flex items-center justify-between px-2 transition ${
          sel 
            ? 'bg-[#333] border-[#333] text-white dark:bg-[#D41534] dark:border-[#D41534]' 
            : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0] dark:bg-[#252525] dark:border-[#333] dark:hover:bg-[#333] dark:text-white'
        }`}
      >
        <span className={`text-[11px] font-bold ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
        <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{oddsVal}</span>
      </button>
    );
  };

  return (
    <div className="w-[310px] min-h-[145px] p-3.5 flex flex-col border border-gray-200 dark:border-[#333] rounded-xl hover:shadow-md transition cursor-pointer relative bg-white dark:bg-[#1a1a1a] group/card">
      {/* Header */}
      <div className="relative -mx-3.5 -mt-3.5 mb-4 h-[40px] flex items-center justify-between px-3.5 border-b border-transparent">
        {/* Slanted Backgrounds (Mobile) */}
        <div className="absolute top-0 left-0 h-full w-[65%] rounded-tl-xl md:hidden" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
        <div className="absolute top-0 left-[calc(65%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom md:hidden" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }} />

        {/* Slanted Backgrounds (Desktop) */}
        <div className="absolute top-0 left-0 h-full w-[65%] rounded-tl-xl hidden md:block" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
        <div className="absolute top-0 left-[calc(65%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom hidden md:block" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }} />

        <div className="flex items-center gap-1.5 text-white relative z-10 pl-1">
          <span className="text-[14px]">{SPORT_EMOJI[event.event.sport_name] || '⚽'}</span>
          <span className="text-[14px] font-bold truncate max-w-[150px] tracking-wide">{event.event.tournament_name}</span>
        </div>
        <div className="text-[12px] font-bold text-gray-500 relative z-10 pr-1">
          {dateDisplay}
        </div>
      </div>
      
      {/* Content - Teams */}
      <div className="flex flex-col gap-2 mb-4 flex-1 justify-center pl-1">
        <div className="flex items-center gap-2">
          <img src={event.event?.home?.crest_url || '/default-crest.svg'} alt="home-crest" className="w-[18px] h-[18px] object-contain flex-shrink-0" />
          <span className="text-[14px] font-medium text-gray-800 truncate pr-2">{event.event?.home?.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <img src={event.event?.away?.crest_url || '/default-crest.svg'} alt="away-crest" className="w-[18px] h-[18px] object-contain flex-shrink-0" />
          <span className="text-[14px] font-medium text-gray-800 truncate pr-2">{event.event?.away?.name}</span>
        </div>
      </div>
      
      {/* Odds */}
      <div className="flex gap-2">
        {renderOddsButton('1', home, 'Heimsieg')}
        {renderOddsButton('X', draw, 'Unentschieden')}
        {renderOddsButton('2', away, 'Auswärtssieg')}
      </div>
    </div>
  );
};
